import type { ColumnDef } from "@tanstack/react-table";
import { History } from "lucide-react";
import { Badge } from "@trillion/ui/components/ui/badge";
import { createActionsColumn } from "@trillion/ui";
import { SortableHeader } from "@trillion/ui/components/admin/sortable-header";
import {
  sortableInstant,
  sortableNumber,
  sortableText,
} from "@trillion/ui/lib/data-grid-sort";
import type {
  EnqueuedJob,
  FailedJob,
  JobDescriptor,
  ProcessingJob,
  ScheduledJob,
  SucceededJob,
} from "@/lib/jobs-types";
import { formatProgress } from "@/lib/run-progress";

export function fmt(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

export function jobLabel(job: JobDescriptor | null): string {
  if (!job) return "—";
  return [job.type, job.method].filter(Boolean).join(".") || "—";
}

function duration(ms: number | null): string {
  if (ms == null) return "—";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

const jobIdCell = { className: "font-mono text-xs text-muted-foreground" } as const;

// Composite display column: no accessor, so getCanSort() is false and the shared header renders it as
// a plain label with no per-tab branching.
const jobCol = <T extends { job: JobDescriptor | null }>(): ColumnDef<T> => ({
  id: "job",
  header: ({ column }) => <SortableHeader column={column} label="Job" />,
  cell: ({ row }) => <span className="text-sm">{jobLabel(row.original.job)}</span>,
});

const idCol = <T extends { job_id: string }>(): ColumnDef<T> => ({
  accessorKey: "job_id",
  header: ({ column }) => <SortableHeader column={column} label="Job id" />,
  sortingFn: "text",
  cell: ({ row }) => <span className={jobIdCell.className}>{row.original.job_id}</span>,
});

/** The finished-run tabs' replay action (sc-114198), shared by the Succeeded and Failed menus. */
const REPLAY_LOG_LABEL = "Replay log";

export function getSucceededColumns(actions: {
  onReplay: (job: SucceededJob) => void;
}): ColumnDef<SucceededJob>[] {
  return [
    idCol<SucceededJob>(),
    jobCol<SucceededJob>(),
    {
      id: "succeeded_at",
      accessorFn: (job) => sortableInstant(job.succeeded_at),
      header: ({ column }) => <SortableHeader column={column} label="Succeeded" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => <span className="text-sm">{fmt(row.original.succeeded_at)}</span>,
    },
    {
      // Sorts on the raw millisecond count, never on the rendered "1.2 s" string.
      id: "total_duration",
      accessorFn: (job) => sortableNumber(job.total_duration),
      header: ({ column }) => <SortableHeader column={column} label="Duration" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{duration(row.original.total_duration)}</span>
      ),
    },
    createActionsColumn<SucceededJob>({
      items: [{ label: REPLAY_LOG_LABEL, icon: History, onSelect: actions.onReplay }],
    }),
  ];
}

export function getProcessingColumns(actions: {
  onDelete: (job: ProcessingJob) => void;
  /** Opens the run's live log; the Processing tab passes it, other callers may omit it. */
  onView?: (job: ProcessingJob) => void;
}): ColumnDef<ProcessingJob>[] {
  return [
    idCol<ProcessingJob>(),
    jobCol<ProcessingJob>(),
    {
      id: "server_id",
      accessorFn: (job) => sortableText(job.server_id),
      header: ({ column }) => <SortableHeader column={column} label="Server" />,
      sortUndefined: "last",
      sortingFn: "text",
      cell: ({ row }) => (
        <span className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{row.original.server_id || "—"}</span>
          {row.original.orphaned && (
            <Badge variant="outline" className="text-destructive">
              Orphaned
            </Badge>
          )}
        </span>
      ),
    },
    {
      id: "started_at",
      accessorFn: (job) => sortableInstant(job.started_at),
      header: ({ column }) => <SortableHeader column={column} label="Started" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => <span className="text-sm">{fmt(row.original.started_at)}</span>,
    },
    {
      // Composite display column: no accessor, so getCanSort() is false (like Job).
      id: "progress",
      header: ({ column }) => <SortableHeader column={column} label="Progress" />,
      cell: ({ row }) => <span className="text-sm">{formatProgress(row.original.progress)}</span>,
    },
    createActionsColumn<ProcessingJob>({
      onView: actions.onView,
      onDelete: actions.onDelete,
      viewLabel: "View log",
      deleteLabel: "Stop & delete",
    }),
  ];
}

export function getScheduledColumns(actions: {
  onDelete: (job: ScheduledJob) => void;
}): ColumnDef<ScheduledJob>[] {
  return [
    idCol<ScheduledJob>(),
    jobCol<ScheduledJob>(),
    {
      id: "scheduled_at",
      accessorFn: (job) => sortableInstant(job.scheduled_at),
      header: ({ column }) => <SortableHeader column={column} label="Scheduled" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => <span className="text-sm">{fmt(row.original.scheduled_at)}</span>,
    },
    {
      id: "enqueue_at",
      accessorFn: (job) => sortableInstant(job.enqueue_at),
      header: ({ column }) => <SortableHeader column={column} label="Runs at" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => <span className="text-sm">{fmt(row.original.enqueue_at)}</span>,
    },
    createActionsColumn<ScheduledJob>({ onDelete: actions.onDelete, deleteLabel: "Delete" }),
  ];
}

export function getEnqueuedColumns(actions: {
  onDelete: (job: EnqueuedJob) => void;
}): ColumnDef<EnqueuedJob>[] {
  return [
    idCol<EnqueuedJob>(),
    jobCol<EnqueuedJob>(),
    {
      id: "state",
      accessorFn: (job) => sortableText(job.state),
      header: ({ column }) => <SortableHeader column={column} label="State" />,
      sortUndefined: "last",
      sortingFn: "text",
      cell: ({ row }) =>
        row.original.state ? (
          <Badge variant="outline">{row.original.state}</Badge>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: "enqueued_at",
      accessorFn: (job) => sortableInstant(job.enqueued_at),
      header: ({ column }) => <SortableHeader column={column} label="Enqueued" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => <span className="text-sm">{fmt(row.original.enqueued_at)}</span>,
    },
    createActionsColumn<EnqueuedJob>({ onDelete: actions.onDelete, deleteLabel: "Delete" }),
  ];
}

export interface FailedColumnActions {
  onInspect: (job: FailedJob) => void;
  onRequeue: (job: FailedJob) => void;
  onDelete: (job: FailedJob) => void;
  /** Opens the finished run's paced log replay. */
  onReplay: (job: FailedJob) => void;
}

export function getFailedColumns(actions: FailedColumnActions): ColumnDef<FailedJob>[] {
  return [
    idCol<FailedJob>(),
    jobCol<FailedJob>(),
    {
      id: "failed_at",
      accessorFn: (job) => sortableInstant(job.failed_at),
      header: ({ column }) => <SortableHeader column={column} label="Failed" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => <span className="text-sm">{fmt(row.original.failed_at)}</span>,
    },
    {
      // Matches the cell's fallback: the exception type, or the plain reason when there is none.
      id: "exception_type",
      accessorFn: (job) => sortableText(job.exception_type ?? job.reason),
      header: ({ column }) => <SortableHeader column={column} label="Exception" />,
      sortUndefined: "last",
      sortingFn: "text",
      cell: ({ row }) =>
        row.original.exception_type ? (
          <Badge variant="outline" className="text-destructive">
            {row.original.exception_type}
          </Badge>
        ) : (
          <span className="text-sm text-muted-foreground">{row.original.reason || "—"}</span>
        ),
    },
    createActionsColumn<FailedJob>({
      onView: actions.onInspect,
      onEdit: actions.onRequeue,
      onDelete: actions.onDelete,
      viewLabel: "Inspect",
      editLabel: "Requeue",
      deleteLabel: "Delete",
      // Rendered between Requeue and Delete: Inspect, Requeue, Replay log, Delete.
      items: [{ label: REPLAY_LOG_LABEL, icon: History, onSelect: actions.onReplay }],
    }),
  ];
}
